/**
 * EMBEDDED consent popups (M365 admin consent, Exchange NDR / journal rule), the red-app way.
 *
 * Standalone, a consent popup lands on titan's own /azure/callback or /ndr/callback page, which
 * reports to the opener and closes itself. Embedded, the popup comes back to the HOST's origin
 * instead, and booting the host (then this container) inside it is what the user would watch for
 * seconds. So, like red-app's third-party-authentication flow:
 *
 *  1. The opener opens a BLANK popup synchronously, while the click still counts as user
 *     activation (a popup opened after an await is what blockers refuse), tags it with a one-off
 *     attempt id in ITS sessionStorage, and only then points it at the consent URL. The tag
 *     survives the round trip through Microsoft and the API because sessionStorage is per window
 *     and per origin, and the popup comes back to the host's origin.
 *  2. The HOST handles the return: an inline script in its index.html, run before any module
 *     loads, finds the tag, announces the popup's URL on a channel named by the attempt id, drops
 *     the tag and closes. (csiq_app: index.html and src/mfe/thqConsentPopup.ts.) Nothing of this
 *     container runs in the popup, which is also why the return URL carries no mount prefix
 *     (see utils/returnUrl.ts).
 *  3. The opener owns the popup: it settles on that announcement, on the user closing the popup,
 *     or on a timeout, and closes the popup itself.
 *
 * ⚠️ CONTRACT WITH THE HOST: `CONSENT_POPUP_SESSION_KEY` and `CONSENT_POPUP_MESSAGE_TYPE` are
 * mirrored in the host, not imported. Change them together.
 */
import type { ConsentResult } from "@/utils/consent";

/** sessionStorage key, in the POPUP, holding the attempt id the opener is waiting on. */
export const CONSENT_POPUP_SESSION_KEY = "titan-consent-popup";

/** `type` of the one message the host posts on the attempt channel. */
export const CONSENT_POPUP_MESSAGE_TYPE = "titan-consent-popup-redirect";

/**
 * Prefix of each popup's window name. One window PER ATTEMPT: the flows only
 * disable their own button, so a second flow can start while the first popup
 * is open, and a shared name would hand it that popup, retag it, and report
 * its consent to the wrong flow.
 */
export const CONSENT_POPUP_WINDOW_NAME = "titan-consent-popup";

export const CONSENT_POPUP_WINDOW_FEATURES =
  "location=yes,height=570,width=520,scrollbars=yes,status=yes";

/**
 * A backstop only: the host closes the popup the moment it comes back. It has
 * to outlast a slow sign-in with MFA plus consent, since reaching it closes a
 * popup the user may still be using.
 */
export const CONSENT_POPUP_TIMEOUT_MS = 15 * 60_000;

const POPUP_CLOSED_POLL_MS = 500;

export type ConsentPopup = { window: Window; attemptId: string };

export type ConsentPopupOutcome =
  | { kind: "redirect"; url: string }
  /** The user closed it before it came back; nothing was announced. */
  | { kind: "closed" };

const newAttemptId = (): string =>
  `consent_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;

export type OpenConsentPopupOptions = {
  /**
   * Text shown in the window while the request that decides where it goes is
   * in flight. It stays blank otherwise, which reads as broken when that
   * request turns out to need no consent and the window just closes again.
   */
  placeholder?: string;
};

/**
 * Opens a blank popup and tags it. Call synchronously from the click handler. Returns null when
 * the browser blocked it. A popup that could not be tagged is still returned: the host then has
 * nothing to announce and the wait ends when the user closes it.
 */
export function openConsentPopup({
  placeholder,
}: OpenConsentPopupOptions = {}): ConsentPopup | null {
  const attemptId = newAttemptId();
  const popup = window.open(
    "about:blank",
    `${CONSENT_POPUP_WINDOW_NAME}-${attemptId}`,
    CONSENT_POPUP_WINDOW_FEATURES,
  );
  if (!popup) return null;

  if (placeholder) {
    // Best effort, as red-app does: a document that cannot be written is still
    // a usable popup.
    try {
      const body = popup.document.body;
      body.style.cssText =
        "margin:0;min-height:100vh;display:flex;align-items:center;justify-content:center;font:14px system-ui,sans-serif;color:#555";
      body.textContent = placeholder;
    } catch {
      /* leave it blank */
    }
  }

  try {
    popup.sessionStorage.setItem(CONSENT_POPUP_SESSION_KEY, attemptId);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn(
      "[titan] could not tag the consent popup; its result cannot be announced.",
      error,
    );
  }
  return { window: popup, attemptId };
}

/**
 * Settles when the host announces where the popup came back (`redirect`), when the user closes it
 * (`closed`), or rejects after `timeoutMs`. The popup and the channel are closed either way.
 */
export function waitForConsentPopup(
  popup: ConsentPopup,
  { timeoutMs = CONSENT_POPUP_TIMEOUT_MS }: { timeoutMs?: number } = {},
): Promise<ConsentPopupOutcome> {
  return new Promise<ConsentPopupOutcome>((resolve, reject) => {
    let settled = false;
    let channel: BroadcastChannel | null = null;
    let poll: ReturnType<typeof setInterval> | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      channel?.close();
      clearInterval(poll);
      clearTimeout(timer);
      try {
        popup.window.close();
      } catch {
        /* already gone */
      }
    };
    const settle = (finish: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      finish();
    };

    try {
      channel = new BroadcastChannel(popup.attemptId);
      channel.onmessage = (event: MessageEvent) => {
        const data = event.data as { type?: unknown; url?: unknown } | null;
        if (
          data?.type === CONSENT_POPUP_MESSAGE_TYPE &&
          typeof data.url === "string"
        ) {
          const url = data.url;
          settle(() => resolve({ kind: "redirect", url }));
        }
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        "[titan] BroadcastChannel unavailable; waiting for the consent popup to close instead.",
        error,
      );
    }

    poll = setInterval(() => {
      if (popup.window.closed) settle(() => resolve({ kind: "closed" }));
    }, POPUP_CLOSED_POLL_MS);

    timer = setTimeout(
      () => settle(() => reject(new Error("consent popup timed out"))),
      timeoutMs,
    );
  });
}

/**
 * The outcome the API encodes in the callback URL it redirects the popup to, or null when the URL
 * carries none (not a callback page, or not a URL at all).
 */
export function parseConsentResult(url: string): ConsentResult | null {
  let params: URLSearchParams;
  try {
    params = new URL(url).searchParams;
  } catch {
    return null;
  }
  const success = params.get("success");
  if (success === "true") return { success: true };
  if (success === "false") {
    return {
      success: false,
      error: params.get("error") || "",
      errorDescription: params.get("errorDescription") || "",
    };
  }
  return null;
}
